import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useAuthStore } from '@/features/auth';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

interface Language {
  code: 'he' | 'en';
  name: string;
  nativeName: string;
  dir: 'rtl' | 'ltr';
}

const languages: Language[] = [
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', dir: 'rtl' },
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
];

/**
 * LanguageSwitcher Component
 * Compact toggle switch for switching languages (Hebrew/English)
 * Matches the VideoGame theme mobile drawer toggle style
 * Persists selection to database and overrides system defaults
 */
export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  // Load user's saved language preference on mount
  useEffect(() => {
    const loadUserLanguage = async () => {
      if (!user) return;

      try {
        const { data } = await api.get('/users/me/language');
        const savedLanguage = data?.data?.preferredLanguage;

        if (savedLanguage && savedLanguage !== i18n.language) {
          await i18n.changeLanguage(savedLanguage);
          const lang = languages.find((l) => l.code === savedLanguage);
          if (lang) {
            document.documentElement.dir = lang.dir;
            document.documentElement.lang = lang.code;
          }
        }
      } catch (error) {
        console.error('Failed to load user language preference:', error);
      }
    };

    loadUserLanguage();
  }, [user, i18n]);

  const handleToggle = async () => {
    if (isLoading) return;

    const newLang = i18n.language === 'he' ? 'en' : 'he';
    const newDir = newLang === 'he' ? 'rtl' : 'ltr';

    setIsLoading(true);

    try {
      await i18n.changeLanguage(newLang);
      document.documentElement.dir = newDir;
      document.documentElement.lang = newLang;

      if (user) {
        await api.patch('/users/me/language', { preferredLanguage: newLang });
      }
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={isLoading}
      className="relative flex items-center rounded-lg overflow-hidden transition-all duration-300"
      style={{
        width: '80px',
        height: '36px',
        backgroundColor: '#DBC299',
        border: '3px solid #000000',
        boxShadow: '2px 2px 0px #000000',
        opacity: isLoading ? 0.6 : 1,
      }}
      aria-label={t('common:a11y.toggleLanguage')}
    >
      {/* Sliding indicator */}
      <motion.div
        className="absolute top-0 bottom-0 rounded-md"
        style={{
          width: '50%',
          backgroundColor: '#F4D35E',
          border: '2px solid #000000',
          margin: '2px',
        }}
        animate={{
          x: i18n.language === 'he' ? 0 : 'calc(100% - 4px)',
        }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      />

      {/* HE label */}
      <span
        className="relative z-10 w-1/2 text-center text-xs font-bold"
        style={{
          color: '#000000',
          opacity: i18n.language === 'he' ? 1 : 0.4,
        }}
      >
        עב
      </span>

      {/* EN label */}
      <span
        className="relative z-10 w-1/2 text-center text-xs font-bold"
        style={{
          color: '#000000',
          opacity: i18n.language === 'en' ? 1 : 0.4,
        }}
      >
        EN
      </span>
    </button>
  );
}
